import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useBackButtonToPrevRoute } from '@/hooks/useBackButtonToPrevRoute.tsx';
import { routes } from '@/navigation/routes.tsx';
import {
  blockContainer,
  menuItemStyle,
  menuTextStyle,
  subtitleStyle
} from '@/pages/Profile/Settings/styles.ts';
import Toggle from '@/components/common/Toggle/Toggle.tsx';
import { useProfileStore } from '@/store/useProfileStore.ts';
import Modal from '@/animations/Modal.tsx';
import PaywallPopup from '@/components/Popup/PaywallPopup/PaywallPopup.tsx';
import {
  PaywallTypes,
  PrivacyLabels,
  ProductInvoicePlacements
} from '@/enums/apiEnums.ts';
import { InputContent } from '@/components/common/InputContent/InputContent.tsx';
import ProfileController from '@/api/profile/profile.ts';
import { UpdatePrivacyPayload } from '@/api/profile/types.ts';
import { catchErrors } from '@/utils/sentry-monitor.tsx';
import PremiumBadge from '@/components/Popup/PaywallPopup/components/PremiumBadge.tsx';

interface PrivacySetting {
  label: PrivacyLabels;
  titleKey: string;
  subtitleKey: string;
}

const Privacy: FC = () => {
  useBackButtonToPrevRoute(routes.profileSettings, false);
  const { t } = useTranslation();
  const { premium, updateProfile, aboutMe } = useProfileStore();
  const [payWallOpen, setPayWallOpen] = useState(false);

  const settings: PrivacySetting[] = [
    {
      label: PrivacyLabels.CONCEALABLE_ATTRIBUTE_AGE,
      titleKey: 'hide_age_title',
      subtitleKey: 'hide_age_subtitle'
    },
    {
      label: PrivacyLabels.CONCEALABLE_ATTRIBUTE_LOCATION,
      titleKey: 'hide_location_title',
      subtitleKey: 'hide_location_subtitle'
    },
    {
      label: PrivacyLabels.CONCEALABLE_ATTRIBUTE_ONLINE_STATUS,
      titleKey: 'hide_online_status_title',
      subtitleKey: 'hide_online_status_subtitle'
    }
  ];

  const concealed = aboutMe?.profile.concealed_attributes || [];

  const handleToggleClick = (isChecked: boolean) => {
    if (!isChecked && premium.isExpired) {
      setPayWallOpen(true);
      return false;
    }
    return true;
  };

  const handleToggleChange =
    (label: PrivacyLabels) => async (checked: boolean) => {
      if (checked && premium.isExpired) {
        return;
      }

      const updatedConcealed = checked
        ? [...concealed, label]
        : concealed.filter((attr) => attr !== label);

      const payload: UpdatePrivacyPayload = {
        concealed_attributes: updatedConcealed
      };

      try {
        await ProfileController.updatePrivacy(payload);
        await updateProfile();
      } catch (e) {
        catchErrors(e, true);
      }
    };

  return (
    <div style={{ height: 'calc(100vh - 100px)', paddingTop: '100px' }}>
      <InputContent
        title={t('privacy_menu_item')}
        footer={t('only_for_premium_users')}
      >
        <div style={blockContainer}>
          {settings.map((setting) => (
            <div key={setting.label} style={menuItemStyle}>
              <div style={{ flexGrow: 1, textAlign: 'left' }}>
                <span style={menuTextStyle}>{t(setting.titleKey)}</span>
                <PremiumBadge text="Premium" />
                <div style={subtitleStyle}>{t(setting.subtitleKey)}</div>
              </div>
              <Toggle
                isChecked={concealed.includes(setting.label)}
                onChange={handleToggleChange(setting.label)}
                onClick={() =>
                  handleToggleClick(concealed.includes(setting.label))
                }
              />
            </div>
          ))}
        </div>
      </InputContent>

      <Modal
        fullScreen
        scrollable
        open={payWallOpen}
        handleClose={() => setPayWallOpen(false)}
      >
        <PaywallPopup
          handleClose={() => setPayWallOpen(false)}
          type={PaywallTypes.TYPE_SUBSCRIPTION}
          placement={ProductInvoicePlacements.PROFILE_SETTINGS_SWITCHERS}
          hideBackButton={false}
          prevHeaderTemplate="light"
        />
      </Modal>
    </div>
  );
};

export default Privacy;
